import React, { useEffect, useState } from "react";
import "../../css/WalletTransactions.css";
import { fetchWalletTransactions } from "../../service/api/adminApi";

const PER_PAGE = 100;

const Arrow = ({ active, dir }) => (
    <span className={`wallet-sort-arrow${active ? " active" : ""}`}>
        {active ? (dir === "asc" ? "↑" : "↓") : "↕"}
    </span>
);

export default function WalletTransactions() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [search, setSearch] = useState("");
    const [type, setType] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [sortBy, setSortBy] = useState("created_at");
    const [sortDir, setSortDir] = useState("desc");

    const loadData = async (targetPage = 1, override = {}) => {
        try {
            setLoading(true);

            const params = {
                page: targetPage,
                per_page: PER_PAGE,
                search: override.search ?? search,
                type: override.type ?? type,
                from_date: override.from_date ?? fromDate,
                to_date: override.to_date ?? toDate,
                sort_by: override.sort_by ?? sortBy,
                sort_dir: override.sort_dir ?? sortDir,
            };

            const response = await fetchWalletTransactions(params);
            setRows(response?.data || []);
            setPage(response?.current_page || 1);
            setLastPage(response?.last_page || 1);
            setTotal(response?.total || 0);
        } catch (e) {
            console.error(e);
            alert("Failed to load wallet transactions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => loadData(1);

    const handleReset = () => {
        setSearch("");
        setType("");
        setFromDate("");
        setToDate("");
        setSortBy("created_at");
        setSortDir("desc");

        loadData(1, {
            search: "",
            type: "",
            from_date: "",
            to_date: "",
            sort_by: "created_at",
            sort_dir: "desc",
        });
    };

    const handleSort = (column) => {
        const nextDir = sortBy === column && sortDir === "asc" ? "desc" : "asc";
        setSortBy(column);
        setSortDir(nextDir);
        loadData(1, { sort_by: column, sort_dir: nextDir });
    };

    const goTo = (nextPage) => {
        if (nextPage < 1 || nextPage > lastPage || loading) return;
        loadData(nextPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const money = (amount) => {
        const value = Number(amount || 0) / 100;
        return `₹${value.toFixed(2)}`;
    };

    const date = (value) => {
        if (!value) return "-";
        return new Date(value).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    return (
        <div className="wallet-page">
            <div className="wallet-head">
                <div>
                    <div className="wallet-title">Wallet Transactions</div>
                    <p className="wallet-subtitle">
                        View and filter all wallet deposits and withdrawals
                    </p>
                </div>

                <button className="wallet-reset-btn" onClick={handleReset}>
                    Reset Filters
                </button>
            </div>

            <div className="wallet-section">
                <div className="wallet-toolbar">
                    <div className="wallet-search-wrap">
                        <span className="wallet-search-icon">⌕</span>
                        <input
                            className="wallet-search"
                            placeholder="Search transaction id / description  / user name / phone"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        />
                        {search && (
                            <button
                                className="wallet-search-clear"
                                onClick={() => {
                                    setSearch("");
                                    loadData(1, { search: "" });
                                }}
                            >
                                ×
                            </button>
                        )}
                    </div>

                    <select
                        className="wallet-filter"
                        value={type}
                        onChange={(e) => {
                            setType(e.target.value);
                            loadData(1, { type: e.target.value });
                        }}
                    >
                        <option value="">All Types</option>
                        <option value="deposit">Deposit</option>
                        <option value="withdraw">Withdraw</option>
                    </select>

                    <input
                        className="wallet-filter"
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />

                    <input
                        className="wallet-filter"
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                    />

                    <button className="wallet-search-btn" onClick={handleSearch}>
                        Search
                    </button>

                    <span className="wallet-count-label">
                        {total.toLocaleString()} Transactions
                    </span>
                </div>

                <div className="wallet-table-wrap">
                    <table className="wallet-table">
                        <thead className="wallet-thead">
                        <tr>
                            <th onClick={() => handleSort("uuid")}>
                                Transaction ID <Arrow active={sortBy === "uuid"} dir={sortDir} />
                            </th>
                            <th onClick={() => handleSort("user")}>
                                User <Arrow active={sortBy === "user"} dir={sortDir} />
                            </th>
                            <th onClick={() => handleSort("user.phone")}>
                                Phone <Arrow active={sortBy === "user.phone"} dir={sortDir} />
                            </th>
                            <th onClick={() => handleSort("type")}>
                                Type <Arrow active={sortBy === "type"} dir={sortDir} />
                            </th>
                            <th onClick={() => handleSort("amount")}>
                                Amount <Arrow active={sortBy === "amount"} dir={sortDir} />
                            </th>
                            <th>Description</th>
                            <th onClick={() => handleSort("created_at")}>
                                Date <Arrow active={sortBy === "created_at"} dir={sortDir} />
                            </th>
                            {/* <th onclick={() => handleSort("status")}></th> */}
                        </tr>
                        </thead>

                        <tbody className="wallet-tbody">
                        {loading ? (
                            Array.from({ length: 6 }).map((_, index) => (
                                <tr key={index} className="wallet-skeleton-row">
                                    {Array.from({ length: 7 }).map((__, i) => (
                                        <td key={i}><div className="wallet-skeleton-block" /></td>
                                    ))}
                                </tr>
                            ))
                        ) : rows.length > 0 ? (
                            rows.map((item) => (
                                <tr key={item.id} className="wallet-row">
                                    <td data-label="Transaction ID">
                                        <strong>{item.uuid || item.id}</strong>
                                    </td>
                                    <td data-label="User">
                                        <div className="wallet-user-name">
                                            {item.user?.name || "-"}
                                        </div>
                                    </td>
                                    <td data-label="Phone">
                                        <div className="wallet-user-phone">
                                            {item.user?.phone || ""}
                                        </div>
                                    </td>


                                    <td data-label="Type">
                                        <span className={`wallet-badge ${item.type === "deposit" ? "deposit" : "withdraw"}`}>
                                            {item.type || "-"}
                                        </span>
                                    </td>

                                    <td data-label="Amount" className="wallet-amount">
                                        {money(item.amount)}
                                    </td>

                                    <td data-label="Description">
                                        {item.description || item?.meta?.description || "-"}
                                    </td>

                                    <td data-label="Date" className="wallet-date">
                                        {date(item.created_at)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={7} className="wallet-empty">
                                    <div className="wallet-empty__icon">💳</div>
                                    <div className="wallet-empty__title">No wallet transactions found</div>
                                    <div className="wallet-empty__sub">Try changing search or filters</div>
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="wallet-pagination">
                    <div className="wallet-pagination__info">
                        Page {page} of {lastPage} | Showing {rows.length} of {total.toLocaleString()} | Per page {PER_PAGE}
                    </div>

                    <div className="wallet-pagination__controls">
                        <button
                            className="wallet-pagination__btn"
                            onClick={() => goTo(page - 1)}
                            disabled={page === 1 || loading}
                        >
                            ← Previous
                        </button>

                        {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
                            const pageNum = page <= 3 ? i + 1 : page - 2 + i;
                            if (pageNum > lastPage) return null;

                            return (
                                <button
                                    key={pageNum}
                                    className={`wallet-pagination__page ${pageNum === page ? "active" : ""}`}
                                    onClick={() => goTo(pageNum)}
                                    disabled={loading}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}

                        <button
                            className="wallet-pagination__btn"
                            onClick={() => goTo(page + 1)}
                            disabled={page === lastPage || loading}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}